import { useState, useCallback } from 'react';
import {
  collection,
  doc,
  getDoc,
  query,
  where,
  getDocs,
  writeBatch,
  updateDoc,
  Timestamp
} from 'firebase/firestore';
import { db, rtdb } from '../lib/firebase';
import { ref, set } from 'firebase/database';
import { Room, Player, Question, Challenge, GameSettings, GameMode } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { useGame } from '../contexts/GameContext';
import { usePresence } from '../contexts/PresenceContext';
import { useToast } from '../contexts/ToastContext';
import { generateRoomCode, isValidRoomCode, createGameSequence, isGameItemChallenge } from '../utils/gameUtils';
import { filterQuestionsForPlayers, filterQuestionsByCategory, resolvePlaceholders } from '../utils/templateUtils';
import { filterChallengesByCategory, resolveChallengeText } from '../utils/challengeUtils';
import {
  createRoomAtomic,
  joinRoomAtomic,
  leaveRoomAsPlayer,
  leaveRoomAsHost
} from '../utils/roomOperations';
import { gameEvents } from '../utils/analytics';
import { useRoomReconnection } from './useRoomReconnection';

export const useRoom = () => {
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();
  const { dispatch, myPlayerId } = useGame();
  const { setPresence, clearPresence } = usePresence();
  const { showError, showSuccess } = useToast();
  const { saveRoomState, clearRoomState } = useRoomReconnection();

  const createRoom = useCallback(async (hostNickname: string, settings: GameSettings, hostPhoto?: string | null, gameMode: GameMode = 'categories'): Promise<string> => {
    if (!user?.uid) {
      throw new Error('Usuario no autenticado');
    }

    setLoading(true);
    try {
      console.log('🌱 ROOM_CREATE: Starting room creation for user:', user.uid);
      console.log('🔍 ROOM_CREATE: Current GameContext state when creating room:', {
        gameContextExists: !!dispatch,
        userUid: user?.uid,
        myPlayerIdInContext: myPlayerId,
        areTheyEqual: user?.uid === myPlayerId
      });
      
      // Wait a moment to let any pending auth state settle
      await new Promise(resolve => setTimeout(resolve, 100));
      console.log('🔥 Firebase config:', { projectId: db.app.options.projectId });
      console.log('🔐 User auth state:', { 
        isAnonymous: user.isAnonymous, 
        providerData: user.providerData,
        creationTime: user.metadata.creationTime,
        lastSignInTime: user.metadata.lastSignInTime
      });
      
      let roomCode: string;
      let attempts = 0;
      const maxAttempts = 10;

      // Generate unique room code with collision detection (SECURITY CRITICAL)
      do {
        roomCode = generateRoomCode();
        console.log(`🎲 Generated room code attempt ${attempts + 1}:`, roomCode);
        
        // CRITICAL: Check if room code already exists to prevent collisions
        const existingRoomRef = doc(db, 'rooms', roomCode);
        const existingRoomSnap = await getDoc(existingRoomRef);
        
        if (!existingRoomSnap.exists()) {
          console.log('✅ Room code is unique and available:', roomCode);
          break; // Code is unique, exit loop
        } else {
          console.log('⚠️  Room code collision detected! Generating new code. Collision with:', roomCode);
          console.log('🔄 Existing room status:', existingRoomSnap.data()?.status);
        }
        
        attempts++;
        if (attempts >= maxAttempts) {
          throw new Error(`No se pudo generar un código único después de ${maxAttempts} intentos. El sistema está muy ocupado, inténtalo en unos segundos.`);
        }
      } while (attempts < maxAttempts);

      const finalRoomCode = roomCode!;

      // Create room + host player atomically (fails on collision).
      const room: Omit<Room, 'code'> = {
        hostId: user.uid,
        status: 'LOBBY',
        createdAt: Timestamp.now(),
        currentRoundIndex: 0,
        maxPlayers: 15,
        playerCount: 1, // Host counts as first player
        settings: settings,
        settingsLocked: false,
        gameMode: gameMode
      };
      const player: Omit<Player, 'id'> = {
        nickname: hostNickname.trim(),
        isHost: true,
        connected: true,
        joinedAt: Timestamp.now(),
        ...(hostPhoto && { profilePhoto: hostPhoto })
      };

      await createRoomAtomic(db, finalRoomCode, room, user.uid, player);

      console.log('✅ ROOM_CREATE: Room created successfully:', {
        roomCode: finalRoomCode,
        hostId: user.uid,
        hostNickname: hostNickname.trim()
      });

      // Immediately reflect room data locally, then register presence.
      dispatch({ type: 'SET_ROOM', payload: { ...room, code: finalRoomCode } as Room });
      await setPresence(finalRoomCode);

      // Track room creation
      gameEvents.roomCreated(settings);
      
      // Save room state for reconnection (as host)
      saveRoomState(finalRoomCode, true);
      
      showSuccess(`Sala ${finalRoomCode} creada exitosamente`);
      return finalRoomCode;
    } catch (error: any) {
      console.error('Error creating room:', error);
      showError(error.message || 'Error al crear la sala');
      throw error;
    } finally {
      setLoading(false);
    }
  }, [user, setPresence, showError, showSuccess]);

  const joinRoom = useCallback(async (roomCode: string, nickname: string, profilePhoto?: string | null): Promise<void> => {
    if (!user?.uid) throw new Error('Usuario no autenticado');
    if (!isValidRoomCode(roomCode)) throw new Error('Código de sala inválido');

    setLoading(true);
    try {
      const roomRef = doc(db, 'rooms', roomCode);
      const roomSnap = await getDoc(roomRef);

      if (!roomSnap.exists()) {
        throw new Error('La sala no existe');
      }

      const room = roomSnap.data() as Room;

      if (room.status !== 'LOBBY') {
        throw new Error('La sala ya ha comenzado o terminado');
      }

      // Check if room is full
      const playersRef = collection(db, 'rooms', roomCode, 'players');
      const playersSnap = await getDocs(playersRef);
      
      if (playersSnap.size >= room.maxPlayers) {
        throw new Error('La sala está llena');
      }

      // Check if nickname is already taken
      const nicknameQuery = query(playersRef, where('nickname', '==', nickname.trim()));
      const nicknameSnap = await getDocs(nicknameQuery);
      
      if (!nicknameSnap.empty) {
        throw new Error('Este nombre ya está en uso en la sala');
      }

      // Add player and bump playerCount with an ATOMIC increment so concurrent
      // joins can't lose updates (previously it was a read-modify-write).
      const player: Omit<Player, 'id'> = {
        nickname: nickname.trim(),
        isHost: false,
        connected: true,
        joinedAt: Timestamp.now(),
        ...(profilePhoto && { profilePhoto: profilePhoto })
      };

      await joinRoomAtomic(db, roomCode, user.uid, player);

      // Register presence in RTDB.
      await setPresence(roomCode);

      gameEvents.roomJoined(room.playerCount + 1);

      // Save room state for reconnection (joining players are never the host).
      saveRoomState(roomCode, false);

      showSuccess(`Te has unido a la sala ${roomCode}`);
    } catch (error: any) {
      console.error('Error joining room:', error);
      showError(error.message || 'Error al unirse a la sala');
      throw error;
    } finally {
      setLoading(false);
    }
  }, [user, setPresence, showError, showSuccess]);

  // Archive room instead of deleting (new default behavior)
  const archiveRoom = useCallback(async (roomCode: string): Promise<void> => {
    if (!user?.uid) throw new Error('Usuario no autenticado');

    setLoading(true);
    try {
      console.log('📦 Archiving room instead of deleting:', roomCode);

      // CRITICAL FIX: Clear state BEFORE any async operations to prevent reconnection
      console.log('🚫 ARCHIVE: Clearing room state to prevent reconnection');
      clearRoomState();

      // CRITICAL FIX: Set intentional clear flag IMMEDIATELY
      sessionStorage.setItem('intentional_clear', 'true');
      console.log('🚫 ARCHIVE: Set intentional_clear flag');

      // Archive the room by updating its status
      const roomRef = doc(db, 'rooms', roomCode);
      await updateDoc(roomRef, {
        status: 'ARCHIVED',
        archived: true,
        archivedAt: Timestamp.now(),
        archivedBy: user.uid
      });

      // Clear presence in RTDB (this is safe to delete as it's just presence)
      try {
        const presenceRef = ref(rtdb, `status/rooms/${roomCode}`);
        await set(presenceRef, null);

        const revealStateRef = ref(rtdb, `revealState/${roomCode}`);
        await set(revealStateRef, null);

        console.log('✅ Cleared RTDB presence data for archived room');
      } catch (error) {
        console.warn('Could not clear RTDB data:', error);
      }

      // Clear local presence and game state
      await clearPresence();
      dispatch({ type: 'RESET_GAME' });

      showSuccess('Sala archivada exitosamente');
    } catch (error: any) {
      console.error('Error archiving room:', error);
      showError(error.message || 'Error al archivar la sala');
      throw error;
    } finally {
      setLoading(false);
    }
  }, [user, clearPresence, dispatch, showError, showSuccess, clearRoomState]);

  const deleteRoom = useCallback(async (roomCode: string): Promise<void> => {
    if (!user?.uid) throw new Error('Usuario no autenticado');

    setLoading(true);
    try {
      const batch = writeBatch(db);

      // Delete all players
      const playersRef = collection(db, 'rooms', roomCode, 'players');
      const playersSnap = await getDocs(playersRef);
      playersSnap.forEach(doc => {
        batch.delete(doc.ref);
      });

      // Delete all rounds and their votes
      const roundsRef = collection(db, 'rooms', roomCode, 'rounds');
      const roundsSnap = await getDocs(roundsRef);
      
      for (const roundDoc of roundsSnap.docs) {
        const votesRef = collection(db, 'rooms', roomCode, 'rounds', roundDoc.id, 'votes');
        const votesSnap = await getDocs(votesRef);
        votesSnap.forEach(voteDoc => {
          batch.delete(voteDoc.ref);
        });
        batch.delete(roundDoc.ref);
      }

      // Delete room
      const roomRef = doc(db, 'rooms', roomCode);
      batch.delete(roomRef);

      await batch.commit();

      // Clear presence
      await clearPresence();

      dispatch({ type: 'RESET_GAME' });
      showSuccess('Sala cerrada y datos eliminados');
    } catch (error: any) {
      console.error('Error deleting room:', error);
      showError(error.message || 'Error al eliminar la sala');
      throw error;
    } finally {
      setLoading(false);
    }
  }, [user, clearPresence, dispatch, showError, showSuccess]);

  const leaveRoom = useCallback(async (roomCode: string): Promise<void> => {
    if (!user?.uid) return;

    // Prevent the reconnection watcher from re-joining us after an explicit exit.
    sessionStorage.setItem('intentional_clear', 'true');

    try {
      const roomRef = doc(db, 'rooms', roomCode);
      const roomSnap = await getDoc(roomRef);

      // Always cancel our own presence (onDisconnect) and mark offline first.
      await clearPresence();

      if (!roomSnap.exists()) {
        dispatch({ type: 'RESET_GAME' });
        clearRoomState();
        return;
      }

      const isHost = roomSnap.data().hostId === user.uid;

      if (isHost) {
        // Host leaving on purpose: hand the room to the earliest-joined
        // remaining player instead of destroying it. Only delete if the host
        // is genuinely the last player.
        const successorId = await leaveRoomAsHost(db, roomCode, user.uid);
        if (successorId === null) {
          await deleteRoom(roomCode);
        } else {
          showSuccess('Has salido. El anfitrión pasó a otro jugador.');
        }
        dispatch({ type: 'RESET_GAME' });
        clearRoomState();
        return;
      }

      // Regular player leaving: atomic decrement of playerCount.
      await leaveRoomAsPlayer(db, roomCode, user.uid);
      dispatch({ type: 'RESET_GAME' });
      clearRoomState();
      showSuccess('Has salido de la sala');
    } catch (error: any) {
      console.error('Error leaving room:', error);
      showError(error.message || 'Error al salir de la sala');
    }
  }, [user, clearPresence, dispatch, showError, showSuccess, deleteRoom, clearRoomState]);

  const startChallengesWriting = useCallback(async (roomCode: string): Promise<void> => {
    if (!user?.uid) throw new Error('Usuario no autenticado');

    setLoading(true);
    try {
      console.log('✍️ Starting challenges writing phase for room:', roomCode);
      
      // Get room data
      const roomRef = doc(db, 'rooms', roomCode);
      const roomSnap = await getDoc(roomRef);
      const roomData = roomSnap.data();
      
      if (!roomData) {
        throw new Error('Sala no encontrada');
      }

      const batch = writeBatch(db);

      // Update room status to writing challenges phase
      batch.update(roomRef, {
        status: 'WRITING_CHALLENGES',
        currentRoundIndex: 0,
        settingsLocked: true
      });

      await batch.commit();
      
      console.log('✅ Challenges writing phase started');
      showSuccess('¡Fase de escritura iniciada!');
    } catch (error: any) {
      console.error('❌ Error starting challenges writing:', error);
      showError(error.message || 'Error al iniciar la fase de escritura');
      throw error;
    } finally {
      setLoading(false);
    }
  }, [user, showError, showSuccess]);

  const startGame = useCallback(async (roomCode: string): Promise<void> => {
    if (!user?.uid) throw new Error('Usuario no autenticado');

    setLoading(true);
    try {
      console.log('🎮 Starting game for room:', roomCode);
      
      // Get questions
      const questionsRef = collection(db, 'questions');
      const questionsQuery = query(questionsRef, where('active', '==', true));
      const questionsSnap = await getDocs(questionsQuery);
      
      // Get challenges  
      const challengesRef = collection(db, 'challenges');
      const challengesQuery = query(challengesRef, where('active', '==', true));
      
      console.log('🔍 About to query challenges from:', {
        projectId: db.app.options.projectId,
        useEmulators: import.meta.env.VITE_USE_EMULATORS
      });
      
      const challengesSnap = await getDocs(challengesQuery);
      
      console.log('📋 Raw queries result:', {
        questions: { empty: questionsSnap.empty, size: questionsSnap.size },
        challenges: { empty: challengesSnap.empty, size: challengesSnap.size }
      });

      if (questionsSnap.empty) {
        console.error('❌ No questions found in database!');
        throw new Error('No hay preguntas disponibles en la base de datos');
      }

      const allQuestions = questionsSnap.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Question[];

      // Use the active-filtered challenges for the game
      const allChallenges = challengesSnap.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Challenge[];
      
      console.log('🔍 Final challenges for game:', {
        activeChallenges: allChallenges.length,
        challengeIds: allChallenges.map(c => c.id),
        challengeCategories: allChallenges.map(c => c.category)
      });
      
      console.log('📝 Content loaded:', {
        questions: {
          total: allQuestions.length,
          active: allQuestions.filter(q => q.active).length,
          templates: allQuestions.filter(q => q.template).length
        },
        challenges: {
          total: allChallenges.length,
          active: allChallenges.filter(c => c.active).length,
          templates: allChallenges.filter(c => c.template).length
        }
      });

      // Get room settings for numRounds
      const roomRef = doc(db, 'rooms', roomCode);
      const roomSnap = await getDoc(roomRef);
      const roomData = roomSnap.data();
      const numRounds = roomData?.settings?.numRounds || 10;
      const roundSeconds = roomData?.settings?.roundSeconds || 15;
      const questionCategory = roomData?.settings?.questionCategory || 'all';
      
      console.log('⚙️ Room settings:', {
        numRounds,
        roundSeconds, 
        questionCategory,
        settingsLocked: roomData?.settingsLocked,
        existingGameQuestions: roomData?.gameQuestions?.length || 0
      });

      // Get current players for filtering
      const playersRef = collection(db, 'rooms', roomCode, 'players');
      const playersSnap = await getDocs(playersRef);
      const players = playersSnap.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Player[];
      
      const connectedPlayers = players.filter(p => p.connected);
      console.log('👥 Players for filtering:', {
        total: players.length,
        connected: connectedPlayers.length,
        names: connectedPlayers.map(p => p.nickname)
      });

      // Filter questions based on category preference
      const categoryQuestions = filterQuestionsByCategory(allQuestions, questionCategory);
      console.log('🏷️ Questions after category filter:', {
        category: questionCategory,
        before: allQuestions.length,
        after: categoryQuestions.length,
        categoriesInFiltered: categoryQuestions.reduce((acc, q) => {
          const cat = q.category || 'undefined';
          acc[cat] = (acc[cat] || 0) + 1;
          return acc;
        }, {} as Record<string, number>)
      });
      
      // Filter challenges based on category preference
      const challengeCategories = questionCategory === 'all' ? 
        ['cotidiano', 'hot', 'spicy', 'extremo'] : // All available challenge categories
        [questionCategory];
      
      const categoryChallenges = filterChallengesByCategory(allChallenges, 
        challengeCategories, 
        connectedPlayers.length
      );
      console.log('🏷️ Challenges after category filter:', {
        category: questionCategory,
        before: allChallenges.length,
        after: categoryChallenges.length
      });
      
      // Filter questions based on available players  
      const availableQuestions = filterQuestionsForPlayers(categoryQuestions, players);
      console.log('👤 Questions after player filter:', {
        before: categoryQuestions.length,
        after: availableQuestions.length,
        playerCount: connectedPlayers.length
      });

      // Challenges are already filtered by minPlayers in filterChallengesByCategory
      const availableChallenges = categoryChallenges;
      console.log('👤 Challenges available:', {
        total: availableChallenges.length,
        playerCount: connectedPlayers.length
      });
      
      // Create mixed game sequence with 30% challenges
      const gameSequence = createGameSequence(availableQuestions, availableChallenges, numRounds, 30);
      console.log('🎲 Final game sequence:', {
        requested: numRounds,
        availableQuestions: availableQuestions.length,
        availableChallenges: availableChallenges.length,
        sequenceLength: gameSequence.length,
        questions: gameSequence.filter(item => !isGameItemChallenge(item)).length,
        challenges: gameSequence.filter(item => isGameItemChallenge(item)).length,
        firstFiveIds: gameSequence.slice(0, 5).map(item => item.id)
      });
      
      if (gameSequence.length === 0) {
        console.error('❌ No content available after filtering!', {
          allQuestions: allQuestions.length,
          allChallenges: allChallenges.length,
          afterCategoryQuestions: categoryQuestions.length,
          afterCategoryChallenges: categoryChallenges.length,
          afterPlayerQuestions: availableQuestions.length,
          availableChallenges: availableChallenges.length,
          settings: { questionCategory, connectedPlayers: connectedPlayers.length }
        });
        throw new Error('No se pudo crear contenido para el juego. Revisa la configuración de categorías.');
      }
      
      // Ensure we have enough content for all rounds
      if (gameSequence.length < numRounds) {
        console.warn('⚠️ Game sequence is shorter than requested rounds!', {
          requested: numRounds,
          generated: gameSequence.length,
          shortfall: numRounds - gameSequence.length
        });
        console.warn('This could cause errors during gameplay. Consider adjusting the challenge percentage or adding more content.');
      }

      const batch = writeBatch(db);

      // Cap the number of rounds to the amount of unique content we actually
      // have, so the game can never get stuck running past the sequence.
      const effectiveRounds = Math.max(1, Math.min(numRounds, gameSequence.length));

      // Update room status and lock settings, store selected game sequence
      batch.update(roomRef, {
        status: 'QUESTION',
        currentRoundIndex: 0,
        settingsLocked: true,
        gameQuestions: gameSequence.map(item => item.id),
        ...(effectiveRounds !== numRounds
          ? { settings: { ...roomData?.settings, numRounds: effectiveRounds } }
          : {})
      });

      // Create first round
      const roundRef = doc(collection(db, 'rooms', roomCode, 'rounds'));
      const firstItem = gameSequence[0];
      const isFirstItemChallenge = isGameItemChallenge(firstItem);

      // For challenges, no deadline. For questions, set a server-based deadline.
      const deadlineAt = isFirstItemChallenge
        ? undefined
        : Timestamp.fromMillis(Date.now() + roundSeconds * 1000);

      // Prepare round data (works for both questions and challenges)
      let itemTextResolved = firstItem.template || '';
      let placeholdersResolved: Record<string, string> = {};
      let itemTemplateId: string | undefined;

      // Resolve placeholders for both questions and challenges
      const itemWithTemplate = firstItem as Question | Challenge;
      if (itemWithTemplate.template && itemWithTemplate.placeholders && itemWithTemplate.placeholders.length > 0) {
        if (isFirstItemChallenge) {
          const resolution = resolveChallengeText(firstItem as Challenge, players);
          itemTextResolved = resolution.resolvedText;
          placeholdersResolved = resolution.placeholdersResolved;
          itemTemplateId = firstItem.id;
        } else {
          const resolution = resolvePlaceholders(itemWithTemplate.template, itemWithTemplate.placeholders, players);
          itemTextResolved = resolution.resolvedText;
          placeholdersResolved = resolution.placeholdersResolved;
          itemTemplateId = itemWithTemplate.id;
        }
      }

      const roundData: any = {
        index: 0,
        questionId: firstItem.id,
        questionText: firstItem.template,
        questionTextResolved: itemTextResolved,
        category: firstItem.category, // Add category for debugging
        status: isFirstItemChallenge ? 'CHALLENGE_DISPLAY' : 'COLLECTING',
        startedAt: Timestamp.now(),
        tally: {},
        winners: [],
        isChallenge: isFirstItemChallenge
      };

      // Add deadline only for questions, not challenges
      if (deadlineAt) {
        roundData.deadlineAt = deadlineAt;
      }

      // Add template-specific fields if it's a template
      if (itemTemplateId) {
        roundData.questionTemplateId = itemTemplateId;
        roundData.placeholdersResolved = placeholdersResolved;
      }

      batch.set(roundRef, roundData);

      await batch.commit();
      
      // Track game started
      const playersCount = players.length;
      gameEvents.gameStarted(playersCount, gameSequence.length);
      
      showSuccess('¡El juego ha comenzado!');
    } catch (error: any) {
      console.error('Error starting game:', error);
      showError(error.message || 'Error al iniciar el juego');
      throw error;
    } finally {
      setLoading(false);
    }
  }, [user, showError, showSuccess]);

  const startImpostorGame = useCallback(async (roomCode: string): Promise<void> => {
    if (!user?.uid) throw new Error('Usuario no autenticado');

    setLoading(true);
    try {
      console.log('🎭 Starting impostor game for room:', roomCode);

      // Get room data
      const roomRef = doc(db, 'rooms', roomCode);
      const roomSnap = await getDoc(roomRef);
      const roomData = roomSnap.data();

      if (!roomData) {
        throw new Error('Sala no encontrada');
      }

      // Get current players
      const playersRef = collection(db, 'rooms', roomCode, 'players');
      const playersSnap = await getDocs(playersRef);
      const players = playersSnap.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Player[];

      const connectedPlayers = players.filter(p => p.connected);

      if (connectedPlayers.length < 3) {
        throw new Error('Se necesitan al menos 3 jugadores para jugar al Impostor');
      }

      console.log('👥 Players ready for impostor game:', {
        total: players.length,
        connected: connectedPlayers.length
      });

      // Load impostor words from JSON
      const impostorWordsModule = await import('../../data/impostor-words.json');
      const allWords = impostorWordsModule.default;

      if (!allWords || allWords.length === 0) {
        throw new Error('No hay palabras disponibles para el modo impostor');
      }

      console.log('📝 Loaded impostor words:', allWords.length);

      // Select random word
      const randomWord = allWords[Math.floor(Math.random() * allWords.length)];

      // Determine number of impostors based on connected player count
      // (matches useImpostor.nextRound: 2 impostors from 6 players onwards).
      const numImpostors = connectedPlayers.length >= 6 ? 2 : 1;
      const shuffledForImpostors = [...connectedPlayers].sort(() => Math.random() - 0.5);
      const impostorIds = shuffledForImpostors.slice(0, numImpostors).map(p => p.id);

      // Select random first player
      const randomFirstPlayer = connectedPlayers[Math.floor(Math.random() * connectedPlayers.length)];

      console.log('🎲 Game setup:', {
        word: randomWord.word,
        numImpostors,
        impostorIds,
        firstPlayerId: randomFirstPlayer.id,
        firstPlayerNickname: randomFirstPlayer.nickname
      });

      const batch = writeBatch(db);

      // Update room status
      batch.update(roomRef, {
        status: 'IMPOSTOR_PLAYING',
        currentRoundIndex: 0,
        settingsLocked: true
      });

      // Create first impostor round
      const roundRef = doc(collection(db, 'rooms', roomCode, 'impostorRounds'));
      const impostorMinutes = roomData.settings?.impostorMinutes || 3; // Get configured minutes or default to 3
      const roundData = {
        index: 0,
        wordId: randomWord.id,
        word: randomWord.word,
        isPlayerName: false,
        impostorIds, // Array (1 or 2) — consistent with the rest of the flow
        impostorsFound: [],
        firstPlayerId: randomFirstPlayer.id,
        startedAt: Timestamp.now(),
        deadlineAt: Timestamp.fromMillis(Date.now() + impostorMinutes * 60 * 1000), // Use configured minutes
        status: 'PLAYING'
      };

      batch.set(roundRef, roundData);

      await batch.commit();

      console.log('✅ Impostor game started');
      showSuccess('¡El juego del Impostor ha comenzado!');
    } catch (error: any) {
      console.error('❌ Error starting impostor game:', error);
      showError(error.message || 'Error al iniciar el modo impostor');
      throw error;
    } finally {
      setLoading(false);
    }
  }, [user, showError, showSuccess]);

  return {
    loading,
    createRoom,
    joinRoom,
    leaveRoom,
    startGame,
    startChallengesWriting,
    startImpostorGame,
    archiveRoom,
    deleteRoom // Keep for emergency cleanup
  };
};